<script setup lang="ts">
import { CheckIcon, ClockIcon, TruckIcon } from 'lucide-vue-next'
import { useIncomingCollects } from '~/composables/collect/useIncomingCollects'
import type { CollectGraphqlFilters } from '~/types/graphql-backend-types/gql-types'
import { AscDescGraphql, CollectStatus } from '~/types/graphql-backend-types/gql-types'
import { formatDateWithHoursI18n } from '~/utils/date'

const { t } = useI18n()
const { isRecycler, isProducer } = storeToRefs(useAuthStore())
const router = useRouter()

const filters = computed(() => {
  const today = new Date()
  const dayOfWeek = today.getDay()

  // Get Monday (first day of week)
  // If Sunday (0), we need to go back 6 days
  // If Monday (1), we go back 0 days, etc.
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
  const monday = new Date(today)
  monday.setDate(today.getDate() + mondayOffset)
  monday.setHours(0, 0, 0, 0)

  // Get Sunday (last day of week)
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  sunday.setHours(23, 59, 59, 999)

  return {
    startsAtFrom: monday,
    startsAtTo: sunday,
    orderBy: { field: 'startsAt', order: AscDescGraphql.Desc },
    status: [CollectStatus.Confirmed],
    // Recycler don't want to see round stops
    ...(isRecycler.value && { isRoundStop: false, hasRound: false }),
  }
}) as ComputedRef<CollectGraphqlFilters>

const { data: incomingCollects, isPending } = useIncomingCollects(filters, 1)

const remainingIncomingCollects = computed(() => {
  if (incomingCollects.value) {
    const doneCollects = incomingCollects.value.collection.filter(collect => collect?.status === CollectStatus.Finished)
    return incomingCollects.value.count - doneCollects.length
  }
  return 0
})

function redirectToCollectsWithCorrectFilters() {
  const dateQueryParamStartsAt = filters.value.startsAtFrom.toISOString()
  const dateQueryParamEndsAt = filters.value.startsAtTo.toISOString()
  router.push(`/collects?startsAt=${dateQueryParamStartsAt}&endsAt=${dateQueryParamEndsAt}&status=${CollectStatus.Confirmed}`)
}

function redirectToCollect(collectId: string) {
  router.push(`/collects/${collectId}`)
}
</script>

<template>
  <Card v-if="!isPending" class="flex grow">
    <template #title>
      <div class="flex items-center gap-3">
        <TruckIcon :size="24" />
        <span class="text-lg font-bold">
          {{ t('global.incoming_collects', { count: remainingIncomingCollects }) }}
        </span>
      </div>

      <div class="ml-auto">
        <EButton
          v-if="incomingCollects?.collection.length && incomingCollects?.collection.length > 0"
          :label="t('global.see_all')"
          class="btn-primary btn-outline"
          size="sm"
          @click="redirectToCollectsWithCorrectFilters"
        />
      </div>
    </template>
    <template #content>
      <div class="bg-base-200 p-4 rounded-lg flex flex-col gap-2">
        <div v-if="incomingCollects?.collection.length === 0">
          {{ t('global.no_incoming_collects') }}
        </div>
        <div
          v-for="collect in incomingCollects?.collection.slice(0, 5)"
          :key="collect?.id"
          class="cursor-pointer p-3 rounded-lg hover:bg-base-300 transition-colors"
          @click="redirectToCollect(collect!.id)"
        >
          <div class="flex flex-row sm:items-center gap-2">
            <div class="flex-1">
              <div class="font-medium">
                {{ formatDateWithHoursI18n(collect?.startsAt, {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour12: false,
                  timeZone: "UTC",
                }) }}
              </div>
            </div>

            <div class="flex gap-2">
              <Badge
                v-if="isProducer"
                color="neutral"
                class="text-xs"
              >
                {{ collect?.isRoundStop ? t('collect.round_stop') : t('collect.spot') }}
              </Badge>

              <Badge
                :color="collect?.status === CollectStatus.Finished ? 'success' : 'warning'"
                class="text-xs"
              >
                <CheckIcon v-if="collect?.status === CollectStatus.Finished" :size="12" />
                <ClockIcon v-else :size="12" />
                {{ collect?.status === CollectStatus.Finished ? t('collect.collected') : t('global.coming_soon') }}
              </Badge>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Card>

  <div v-else class="skeleton h-full w-full rounded-xl" />
</template>
